import React, { useEffect, useState, useRef } from 'react';
import cx from 'classnames';

import styles from './SearchInput.module.scss';
import SearchInputProps, { SearchOption } from './SearchInput.types';
import OptionsList from './SearchOptions/SearchOptions';

const SearchInput = (props: SearchInputProps) => {
  const {
    options = [],
    valueToShow,
    emptyOption,
    placeholder,
    onChangeInputText,
    onSelectEmptyOption,
    optionListDisabled,
    onChange,
    autoComplete,
    selectedValue = '',
    className,
    dataCy = 'searchInput',
    invalid,
    ariaLabel,
    errorMsgHolderId,
    nullText,
    remote,
    initialiseAsNull,
  } = props;

  const initialValue = initialiseAsNull ? null : selectedValue;

  const initialSelectedText = !initialValue ? nullText : '';

  const searchInputRef = useRef(null);
  const optionsContainerRef = useRef(null);
  const searchRef = useRef(null);
  const selectId = useRef(`id_${Math.random() * 1000}`);

  const [selectValue, setSelectValue] = useState<string>(initialValue);
  const [optionIndex, setOptionIndex] = useState<number>(-1);
  const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
  const [optionList, setOptionList] = useState<SearchOption[]>(options);

  const openOptions = () => setOptionsOpen(true);
  const closeOptions = () => setOptionsOpen(false);

  useEffect(() => {
    if (remote) {
      setOptionList(options);
    }
  }, [options]);

  useEffect(() => {
    if (onChange) onChange(selectValue);
  }, [selectValue]);

  const search = (value: string) => {
    if (onChangeInputText) onChangeInputText();
    setSelectValue(value);
    openOptions();
    if (!remote && value.length > 0) {
      const regex = new RegExp(`^${value}`, `i`);
      const suggestions = options.filter((option) => {
        const v = typeof option !== 'string' ? `${option.const || option.title}` : option;
        return regex.test(v);
      });
      setOptionList(suggestions);
    } else {
      setOptionList(options);
    }
  };

  const handleSelect = (value) => {
    if (!optionListDisabled) {
      closeOptions();
      setSelectValue(value);
    } else if (onSelectEmptyOption) {
      onSelectEmptyOption();
    }
  };

  const combineNullWithOptions = (props: SearchInputProps): SearchInputProps => {
    const initialOption: SearchOption = {
      const: null,
      title: initialSelectedText,
    };

    const optionsWithNull = initialiseAsNull ? [initialOption, ...options] : options;

    return { ...props, options: optionsWithNull };
  };

  const parentOptionProps = combineNullWithOptions(props);

  const detectKey = (event: KeyboardEvent) => {
    return {
      isSpace: event.key === ' ' || event.key === 'Spacebar',
      isEnter: event.key === 'Enter',
      isTab: event.key === 'Tab',
      isEscape: event.key === 'Escape',
      up: event.key === 'ArrowUp',
      down: event.key === 'ArrowDown',
      right: event.key === 'ArrowRight',
      left: event.key === 'ArrowLeft',
    };
  };

  const handleSelectText = (value: string, text: string): void => {
    closeOptions();
    setSelectValue(value || text);
    searchInputRef.current.focus();
  };

  const handleFocus = (event: Event): void => {
    const optionsList = optionsContainerRef.current.children;
    const selectedOptionIndex = [...optionsList].indexOf(event.target);
    setOptionIndex(selectedOptionIndex);
  };

  const handleDirectionKeys = ({ up, down, right, left }, optionsContainer) => {
    const optionsList = optionsContainer.current.children;
    const previous = up || left;
    const next = down || right;

    if (previous) {
      const previousElem = optionsList[optionIndex - 1];
      if (optionIndex - 1 < 0) return;
      previousElem.focus();
    }

    if (next) {
      const nextElem = optionsList[optionIndex + 1];
      if (optionIndex + 1 >= parentOptionProps.options.length) return;
      nextElem.focus();
    }
  };

  const handleOptionSelection = ({ isSpace, isEnter, isEscape }, value, text, optionsContainer) => {
    const spaceOrEnter = isSpace || isEnter;
    if (isEscape) {
      closeOptions();
      searchInputRef.current.focus();
    } else if (value && spaceOrEnter) {
      handleSelectText(value, text);
    } else if (spaceOrEnter) {
      if (optionsContainer?.current?.children) {
        const selectedOption = [...optionsContainer.current.children].filter((el) => el.innerText === selectValue);
        setOptionsOpen(!optionsOpen);
        setTimeout(() => {
          if (selectedOption && selectedOption[0]) {
            selectedOption[0].focus();
          }
        }, 0);
      }
    }
  };

  const handleKeyDown = (event, value, text = null) => {
    const { isSpace, isEnter, isEscape, up, down, right, left } = detectKey(event);
    handleOptionSelection({ isSpace, isEnter, isEscape }, value, text, optionsContainerRef);
    const isDirectionKey = up || down || right || left;
    if (optionsOpen && isDirectionKey) {
      event.preventDefault();
      handleDirectionKeys({ up, down, right, left }, optionsContainerRef);
    }
  };

  const renderSearchList = (searchListProps) => (
    <OptionsList {...parentOptionProps} {...searchListProps} id={selectId.current} />
  );

  return (
    <div className={cx(styles.graphicalInputWrapper, className)} ref={searchRef}>
      <input
        className={styles.graphicalInput}
        type='text'
        placeholder={placeholder}
        onChange={(e) => search(e.target.value)}
        onKeyDown={(event) => handleKeyDown(event, null)}
        value={valueToShow || selectValue}
        autoComplete={autoComplete}
        ref={searchInputRef}
        data-cy={dataCy}
        aria-invalid={invalid}
        aria-label={ariaLabel}
        role='combobox'
        aria-expanded={optionsOpen}
        aria-controls={selectId.current}
        aria-errormessage={errorMsgHolderId}
      />
      {renderSearchList({
        selectValue,
        setSelectValue,
        optionsOpen,
        closeOptions,
        optionsContainerRef,
        onSelect: handleSelect,
        onKeyDown: handleKeyDown,
        onFocus: handleFocus,
        options: optionList,
        disabled: optionListDisabled,
        search: true,
        emptyOption,
        onSelectEmptyOption,
        searchRef,
      })}
    </div>
  );
};

export default SearchInput;
